import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, ArrowRight, Info } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';
import TypedText from '@/components/TypedText';

function WizardStepperV2({ active }) {
  const steps = ['Basic Details', 'Assign Roles', 'Review'];
  return (
    <div className="px-6 py-4 bg-[#F7F7F7] rounded-xl mb-6">
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                i < active ? 'bg-teal-600 border-teal-600' :
                i === active ? 'bg-white border-teal-600' :
                'bg-white border-[#D1D5DB]'
              }`}>
                {i < active
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : i === active
                  ? <div className="w-1.5 h-1.5 rounded-full bg-teal-600" />
                  : null}
              </div>
              <span className={`text-[11.5px] font-body whitespace-nowrap ${i === active ? 'font-bold text-[#0A0A0A]' : i < active ? 'text-[#0A0A0A] font-semibold' : 'text-[#6B7280]'}`}>{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-[2px] mx-2 mt-[-16px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function TypingField({ label, value, required, delay, prefix, flag, instant }) {
  return (
    <div>
      <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <motion.div
        initial={{ borderColor: '#E5E7EB' }}
        animate={instant ? {} : { borderColor: ['#E5E7EB', '#0D9488', '#E5E7EB'] }}
        transition={{ duration: 0.8, delay }}
        className="w-full px-3.5 py-2.5 rounded-lg border bg-white text-[13px] font-body text-[#0A0A0A] flex items-center gap-2 min-h-[42px]"
      >
        {flag && <span className="text-base">{flag}</span>}
        {prefix && <span className="text-[#6B7280]">{prefix}</span>}
        {instant ? <span>{value}</span> : <TypedText text={value} delay={delay} speed={20} />}
      </motion.div>
    </div>
  );
}

function SelectV2({ label, value, openAt, selectAt, required, instant }) {
  const [showValue, setShowValue] = useState(instant);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (instant) return;
    const o = setTimeout(() => setOpen(true), openAt * 1000);
    const c = setTimeout(() => setOpen(false), (openAt + 0.9) * 1000);
    const v = setTimeout(() => setShowValue(true), selectAt * 1000);
    return () => { clearTimeout(o); clearTimeout(c); clearTimeout(v); };
  }, [openAt, selectAt, instant]);
  return (
    <div>
      <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <motion.div
        animate={{ borderColor: open ? '#0D9488' : '#E5E7EB', boxShadow: open ? '0 0 0 3px rgba(13,148,136,0.2)' : '0 0 0 0 transparent' }}
        className="w-full px-3.5 py-2.5 rounded-lg border bg-white text-[13px] font-body flex items-center justify-between min-h-[42px]"
      >
        {showValue
          ? <motion.span initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-[#0A0A0A]">{value}</motion.span>
          : <span className="text-[#9CA3AF]">Select {label.toLowerCase()}</span>}
        <ChevronDown size={15} className="text-[#6B7280]" />
      </motion.div>
    </div>
  );
}

function FacilityTagsV2({ label, values, openAt, addDelays, required, instant }) {
  const [visibleCount, setVisibleCount] = useState(instant ? values.length : 0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (instant) return;
    const o = setTimeout(() => setOpen(true), openAt * 1000);
    const c = setTimeout(() => setOpen(false), (openAt + (addDelays[addDelays.length - 1] || 0) + 0.5) * 1000);
    const timers = addDelays.map((d, i) => setTimeout(() => setVisibleCount(i + 1), (openAt + d) * 1000));
    return () => { clearTimeout(o); clearTimeout(c); timers.forEach(clearTimeout); };
  }, [openAt, addDelays, instant]);
  return (
    <div>
      <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <motion.div
        animate={{ borderColor: open ? '#0D9488' : '#E5E7EB', boxShadow: open ? '0 0 0 3px rgba(13,148,136,0.2)' : '0 0 0 0 transparent' }}
        className="w-full px-3 py-2 rounded-lg border bg-white text-[13px] font-body flex items-center gap-2 flex-wrap min-h-[42px]"
      >
        <AnimatePresence>
          {values.slice(0, visibleCount).map(v => (
            <motion.span key={v}
              initial={{ opacity: 0, scale: 0.6, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
              className="px-2.5 py-1 rounded-md bg-teal-600/10 text-teal-700 text-[11.5px] font-medium flex items-center gap-1"
            >
              {v} <span className="text-teal-600">×</span>
            </motion.span>
          ))}
        </AnimatePresence>
        {visibleCount < values.length && <span className="text-[#9CA3AF] text-[12px]">Select facility</span>}
      </motion.div>
    </div>
  );
}

function FacilityExplainerV2({ delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="mt-6 rounded-2xl border-2 border-teal-600/30 bg-gradient-to-br from-teal-50 to-white p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Info size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-bold text-[15px] text-[#0A0A0A] mb-2">How facility selection works</h4>
          <div className="space-y-2.5 text-[12.5px] font-body text-[#374151] leading-relaxed">
            <div className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
              <p>
                <span className="font-semibold text-teal-700">Facility under an organisation </span>
                — pick the <span className="font-medium">Organisation</span> first (e.g. <span className="px-1.5 py-0.5 rounded bg-white border border-teal-200 text-teal-700 text-[11px] font-medium">Burjeel</span>).
                The <span className="font-medium">Linked facility</span> dropdown then shows only facilities inside it. A user can link to multiple.
              </p>
            </div>
            <div className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
              <p>
                <span className="font-semibold text-amber-700">Standalone facility </span>
                — if the facility is not part of an organisation, the Organisation field is <span className="font-medium">skipped</span>. You select the standalone facility directly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function QuickFillBannerV2() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      className="mt-3 mb-4 rounded-xl bg-teal-50 border border-teal-600/30 px-4 py-2.5 flex items-center gap-3"
    >
      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
        <span className="text-white text-[11px] font-bold">i</span>
      </div>
      <p className="font-body text-[12px] text-teal-900">
        Basic details are already filled for <span className="font-semibold">Ahmed Shareef</span>. Continuing to role assignment…
      </p>
    </motion.div>
  );
}

export default function UserFormBasicV2Scene({ onNext, facilityFocus = false, quickFill = false }) {
  const T = {
    firstName: 4.2, lastName: 4.6, username: 5.0, email: 5.0, contact: 6.6,
    userTypeOpen: 7.2, userTypeSelect: 7.8, nationality: 8.0, eid: 8.6,
    orgOpen: 9.4, orgSelect: 10.0, facilityOpen: 10.3, facilityDelays: [0.4, 1.2],
    nextButton: 12.0,
  };

  const cursorPath = quickFill
    ? [
        { x: 600, y: 260, delay: 0.6 },
        { x: 560, y: 650, delay: 1.0, click: true },
      ]
    : facilityFocus
    ? [
        { x: 400, y: 140,  delay: 0.6 },
        { x: 400, y: 280, delay: 1.0 },
        { x: 800, y: 360, delay: 1.4 },
        { x: 400, y: 520, delay: 5.8 },
        { x: 800, y: 520, delay: 3.0 },
        { x: 800, y: 520, delay: 2.5 },
      ]
    : [
        { x: 400, y: 140,  delay: 0.6 },
        { x: 400, y: 280, delay: 1.2 },
        { x: 800, y: 360, delay: 1.6 },
        { x: 400, y: 520, delay: 1.6 },
        { x: 800, y: 520, delay: 2.0 },
        { x: 560, y: 680, delay: 1.6, click: true },
      ];

  return (
    <PortalShellV2
      variant="headerOnly"
      overlay={cursorPath.length ? <AutoCursor targets={cursorPath} startDelay={facilityFocus ? 0.4 : 0.8} /> : null}
    >
      <div data-testid="scene-user-form-basic-v2" className="px-10 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11.5px] font-body text-[#6B7280] mb-1.5 flex items-center gap-1">
            Manage <ChevronRight size={10} className="inline" /> Users <ChevronRight size={10} className="inline" /> <span className="text-[#0A0A0A]">Add new user</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="mb-1">
            <h1 className="font-heading font-extrabold text-[30px] leading-tight tracking-tight text-[#0A0A0A] flex items-center gap-2">
              Add New User
              {!facilityFocus && !quickFill && (
                <InfoSpot
                  text="Step 1 of 3 — Basic Details. Fill personal info, organisation and linked facilities. Users can belong to multiple facilities in the same organisation."
                  position="right"
                  autoOpenAt={0.8}
                  autoCloseAfter={3.0}
                  glowAt={0.5}
                />
              )}
            </h1>
            <p className="mt-0.5 font-body text-[12px] text-[#6B7280]">Create a new user and set access permissions</p>
          </motion.div>

          <div className="border-b border-[#E5E7EB] mt-4 mb-5" />

          {quickFill && <QuickFillBannerV2 />}

          <WizardStepperV2 active={0} />

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading font-extrabold text-[18px] text-[#0A0A0A]">Basic details</h2>
            <p className="font-body text-[12px] text-[#6B7280] mb-4">Fill in the required basic information</p>

            <div className="grid grid-cols-2 gap-x-6 gap-y-3.5">
              <TypingField label="First name" value="Ahmed" required delay={T.firstName} instant={quickFill} />
              <TypingField label="Last name" value="Shareef" required delay={T.lastName} instant={quickFill} />
              <TypingField label="Username" value="asamoe@yourhealth.com" delay={T.username} instant={quickFill} />
              <TypingField label="Business email address" value="asamoe@yourhealth.com" required delay={T.email} instant={quickFill} />
              <TypingField label="Contact number" value="512 100999" required prefix="+971" delay={T.contact} instant={quickFill} />
              <SelectV2 label="User type" value="Doctor" openAt={T.userTypeOpen} selectAt={T.userTypeSelect} instant={quickFill} />
              <TypingField label="Nationality" value="Algeria" delay={T.nationality} flag="🇩🇿" instant={quickFill} />
              <TypingField label="Emirates ID number" value="784-1939-1111111-1" required delay={T.eid} instant={quickFill} />
              <SelectV2 label="Organisation" value="Burjeel" required openAt={T.orgOpen} selectAt={T.orgSelect} instant={quickFill} />
              <FacilityTagsV2 label="Linked facility" required values={['Burjeel Al Nahyan', 'Burjeel - Mussafah']} openAt={T.facilityOpen} addDelays={T.facilityDelays} instant={quickFill} />
            </div>
          </motion.div>

          {facilityFocus && <FacilityExplainerV2 delay={T.nextButton} />}

          {!facilityFocus && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: quickFill ? 1.2 : T.nextButton }}
              className="flex justify-center mt-6"
            >
              <motion.button
                data-testid="wizard-next-basic-v2"
                onClick={onNext}
                animate={{
                  scale: [1, 1, 1.08, 0.97, 1.05, 1],
                  boxShadow: [
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 10px rgba(13,148,136,0.3)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 8px rgba(13,148,136,0.25)',
                    '0 0 0 0 rgba(13,148,136,0)',
                  ],
                }}
                transition={{ duration: 2.4, delay: (quickFill ? 1.4 : T.nextButton + 0.2), times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
                className="flex items-center gap-2 px-10 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-[12.5px] hover:bg-teal-700 transition-colors"
              >
                Next <ArrowRight size={13} />
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>
    </PortalShellV2>
  );
}
